import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useEffect, useState } from "react";
import BasicButton from "@components/button/BasicButton";
import { useApiRequest } from "@utils/ApiRequest";
import BasicModal from "@components/modal/BasicModal";
import useOrderIdStore from "@store/order/useOrderIdStore";
import { useMutation, useQuery } from "@tanstack/react-query";
import useUserInfoStore from "@store/useUserInfoStore";

type Props = {
  totalPrice: number; // 결제 총액
  title: string; // 버튼 타이틀
  buyerName: string; // 구매자 이름
  productName: string; // 상품명
  paymentData: any; // 결제 데이터
  onPaymentStart: (data: any) => Promise<{ order_id: string }>; // 결제 시작 핸들러
  children?: React.ReactNode; // 자식 컴포넌트
  setAddressError?: (error: string) => void; // 주소 오류 설정
};

const TossPaymentButton = ({
  totalPrice,
  title,
  buyerName,
  productName,
  paymentData,
  onPaymentStart,
  children,
  setAddressError,
}: Props) => {
  const { deletePayment, getExchange } = useApiRequest();
  const [widgets, setWidgets] = useState<any>(null); // 토스 결제 위젯 인스턴스
  const clientKey = process.env.REACT_APP_TOSS_PAYMENT_CLIENTKEY as string; // 토스페이먼츠 클라이언트 키
  const [showWidget, setShowWidget] = useState(false);
  const { clearOrderId } = useOrderIdStore();
  const lang = localStorage.getItem("language");
  const { userInfo } = useUserInfoStore();
  const customerKey = userInfo?.toss_customer_key || "";

  // 결제창 -> 결제버튼(주문등록 (orderId반환) -> 성공 시 결제성공페이지 이동(결제승인요청 -> 성공시 store삭제) -> 실패 시 실패페이지 이동(주문취소요청 -> store삭제)

  // console.log("userInfo", userInfo);
  const { mutate } = useMutation({
    mutationFn: (orderId: number) => deletePayment(orderId),
    onSuccess: () => {
      clearOrderId();
    },
  });

  const { data: totalPriceUsd } = useQuery({
    queryKey: ["exchange", totalPrice],
    queryFn: () => getExchange(totalPrice),
  });

  // 토스페이먼츠 SDK 초기화
  useEffect(() => {
    async function initializeWidget() {
      if (!showWidget) return;

      // 토스페이먼츠 SDK 로드 및 위젯 인스턴스 생성
      const tossPayments = await loadTossPayments(clientKey);
      const widgetInstance = tossPayments.widgets({
        customerKey,
      });
      setWidgets(widgetInstance);
    }
    initializeWidget();
  }, [customerKey, showWidget]);

  // 결제 위젯 렌더링
  useEffect(() => {
    async function renderWidget() {
      if (!widgets || !showWidget) return;

      const currency = lang === "en" ? "USD" : "KRW";
      const value = lang === "en" ? totalPriceUsd : totalPrice;

      try {
        // 결제 금액 설정
        await widgets.setAmount({
          currency: currency,
          value: value,
        });

        if (lang === "en") {
          // 결제수단 선택 UI 렌더링
          await widgets.renderPaymentMethods({
            selector: "#payment-method",
            variantKey: "PAYPAL",
          });
        } else {
          // 결제수단 선택 UI 렌더링
          await widgets.renderPaymentMethods({
            selector: "#payment-method",
            variantKey: "DEFAULT",
          });

          // 약관동의 UI 렌더링
          await widgets.renderAgreement({
            selector: "#agreement",
            variantKey: "AGREEMENT",
          });
        }
      } catch (error) {
        console.error("Failed to render payment widget:", error);
      }
    }
    renderWidget();
  }, [widgets, totalPrice, showWidget, lang]);

  // 결제 처리 핸들러
  const handleWidgetPayment = async () => {
    if (!widgets) return;

    const { order_id } = await onPaymentStart(paymentData);

    // console.log("결제요청 시 orderId", order_id);

    const tossOrderId = `ORDER_${order_id}`;

    // console.log("tossOrderId", tossOrderId);
    try {
      const method = lang === "en" ? "P" : "C";

      // 결제 요청
      await widgets.requestPayment({
        orderId: tossOrderId, // 주문 ID
        orderName: productName, // 주문명
        customerName: buyerName, // 구매자명
        successUrl: `${window.location.origin}/success?method=${method}`,
        failUrl: `${window.location.origin}/fail`,
      });
    } catch (error: any) {
      await mutate(Number(order_id)); // 주문 삭제
      window.location.href = `${window.location.origin}/fail`; // 실패 페이지 이동
    }
  };

  // 결제 버튼 클릭 핸들러 - 모달 표시
  const handlePayment = async () => {
    if (userInfo?.shipping_postcode !== null) {
      setShowWidget(true);
    } else if (userInfo?.shipping_postcode === null) {
      setAddressError?.("배송지를 입력해주세요.");
    }
  };

  return (
    <div>
      <BasicButton
        title={title}
        width="33.5rem"
        height="5.2rem"
        onClick={handlePayment}
        gap="0rem"
        fontSize="1.6rem">
        {children}
      </BasicButton>

      {/* 모달 스타일의 위젯 */}
      {showWidget && (
        <>
          <BasicModal
            open={showWidget}
            setOpen={setShowWidget}
            padding="2rem"
            activeName="결제하기"
            closeName="취소"
            closeEvent={() => setShowWidget(false)}
            activeEvent={handleWidgetPayment}>
            <div id="payment-method" style={{ width: "100%" }} />
            <div id="agreement" style={{ width: "100%" }} />
          </BasicModal>
        </>
      )}
    </div>
  );
};

export default TossPaymentButton;
